import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import { useLocalStorageNew } from "../../../hooks/useLocalStorageNew";
import {
    GET_CHROME_EXT_MESSAGE,
    LOGOUT_CHROME_EXT_MESSAGE,
    MENTIMETER,
    MENTIMETER_EXTENSION_ID,
    POLL_EVERYWHERE,
    POLL_EVERYWHERE_EXTENSION_ID,
    SESSION,
    TOPHAT,
    TOPHAT_EXTENSION_ID,
    WOOCLAP,
    WOOCLAP_EXTENSION_ID,
} from "../../../utils/constants";
import styles from "./CrossIntegration.module.css";
import YuJaButton from "../../../components/standardization/YuJaButton";
import {YuJaDropdown} from "../../../components/standardization/YuJaDropdown";
import {ReactComponent as Checkmark} from "../../../images/ext_integration_checkmark.svg";
import THExt from "../../../images/tophat.svg";
import MExt from "../../../images/mentimeter.svg";
import PEExt from "../../../images/polleverywhere.svg";
import WCExt from "../../../images/wooclap.svg";
import Extension from "../../../images/extension.svg";
import {isChromiumBrowser, notifyError, sendMsgToExtension} from "../../../utils/helpers";
import {useConfig} from "../../../hooks/useConfig";

export default function CrossIntegration() {
    const { t } = useTranslation();
    const [hasConnection, setHasConnection] = useState(false);
    const [extInstalled, setExtInstalled] = useState(false);
    const [, getSession] = useLocalStorageNew(SESSION, {});
    const [currPlatform, setCurrPlatform] = useState(MENTIMETER);
    // Wooclap is hidden in production until its first version is released
    const isProduction = ["PROD_UW2", "PROD_CA1"].includes(process.env.REACT_APP_RUN_ENVIRONMENT);
    const integratedProducts = [
        {label: t("poll.mentimeterLabel"), value: MENTIMETER},
        {label: t("poll.topHatLabel"), value: TOPHAT},
        {label: t("poll.pollEverywhereLabel"), value: POLL_EVERYWHERE},
        ...(isProduction ? [] : [{label: t("poll.wooclapLabel"), value: WOOCLAP}])
    ];
    const session = getSession();
    let config = useConfig();

    const getStepTitle = () => {
        return t("poll.accessPrefix") + currPlatform + t("poll.migrateQuestionsSuffix");
    };

    const getExtUrl = () => {
        if(currPlatform === TOPHAT) {
            return config.migration_chrome_extension.top_hat;
        }
        else if(currPlatform === MENTIMETER) {
            return config.migration_chrome_extension.mentimeter;
        }
        else if(currPlatform === POLL_EVERYWHERE) {
            return config.migration_chrome_extension.poll_everywhere;
        }
        else if(currPlatform === WOOCLAP) {
            return config.migration_chrome_extension.wooclap;
        }
    };

    const getExtSupport = () => {
        if(currPlatform === TOPHAT) {
            return config.migration_chrome_extension.top_hat_support_url;
        }
        else if(currPlatform === MENTIMETER) {
            return config.migration_chrome_extension.mentimeter_support_url;
        }
        else if(currPlatform === POLL_EVERYWHERE) {
            return config.migration_chrome_extension.poll_everywhere_support_url;
        }
        else if(currPlatform === WOOCLAP) {
            return config.migration_chrome_extension.wooclap_support_url;
        }
    };
    
    const getPlatformSteps = () => {
        if(currPlatform === TOPHAT) {
            return t("poll.topHatSteps", {returnObjects: true})
        }
        else if(currPlatform === MENTIMETER) {
            return t("poll.mentimeterSteps", {returnObjects: true})
        }
        else if(currPlatform === POLL_EVERYWHERE) {
            return t("poll.pollEverywhereSteps", {returnObjects: true});
        }
        else if(currPlatform === WOOCLAP) {
            return t("poll.wooclapSteps", {returnObjects: true});
        }
    };

    const getPlatformImage = () => {
        if(currPlatform === TOPHAT) {
            return THExt;
        }
        else if(currPlatform === MENTIMETER) {
            return MExt;
        }
        else if(currPlatform === POLL_EVERYWHERE) {
            return PEExt;
        }
        else if(currPlatform === WOOCLAP) {
            return WCExt;
        }
    };
    
    const steps = [
        {
            title: t("poll.installExtensionStep"),
            subtitle: t("poll.installExtensionSubtitle"),
            button: t("poll.installExtensionButton"),
            completed: t("poll.installExtensionCompletedStep"),
            img: Extension
        },
        {
            title: t("poll.connectExtensionStep"),
            subtitle: t("poll.connectExtensionSubtitle"),
            button: t("poll.connectExtensionButton"),
            completed: t("poll.connectExtensionCompletedStep"),
            img: Extension
        },
        {
            title: getStepTitle(),
            substeps: getPlatformSteps(),
            img: getPlatformImage()
        }
    ];

    const getExtensionId = () => {
        if(currPlatform === TOPHAT) {
            return TOPHAT_EXTENSION_ID;
        }
        else if(currPlatform === MENTIMETER) {
            return MENTIMETER_EXTENSION_ID;
        }
        else if(currPlatform === POLL_EVERYWHERE) {
            return POLL_EVERYWHERE_EXTENSION_ID;
        }
        else if(currPlatform === WOOCLAP) {
            return WOOCLAP_EXTENSION_ID;
        }
    };

    useEffect(() => {
        // 1. Get authorization token and userId from chrome extension
        try {
            if (!window.chrome.runtime) {
                return;
            }

            sendMsgToExtension(getExtensionId(), {info: GET_CHROME_EXT_MESSAGE}).then(response => {
                if(response) {
                    setExtInstalled(true);
                    if(response.authToken !== session.authorization
                        || response.userId !== session.userId
                        || response.api !== config.api.poll
                    ) {
                        sendMsgToExtension(getExtensionId(), {info: LOGOUT_CHROME_EXT_MESSAGE});
                        setHasConnection(false);
                    }
                    else {
                        setHasConnection(true);
                    }

                } else {
                    setHasConnection(false);
                    setExtInstalled(false);
                }
            })

            // window.chrome.runtime.sendMessage(getExtensionId(), {info: GET_CHROME_EXT_MESSAGE}, function(response) {
            //     // 2. If no response, or authorization token is invalid - ask user to connect again
            //     if(response) {
            //         setExtInstalled(true);
            //         if(response.authToken !== session.authorization || response.userId !== session.userId) {
            //             window.chrome.runtime.sendMessage(getExtensionId(), {logout: LOGOUT_CHROME_EXT_MESSAGE}, function(response) {})
            //             setHasConnection(false);
            //         }
            //         else {
            //             setHasConnection(true);
            //         }
            //
            //     } else {
            //         setHasConnection(false);
            //         setExtInstalled(false);
            //     }
            // })
        }
        catch(e) {
            setHasConnection(false);
            setExtInstalled(false);
        }
    }, [currPlatform]);
    
    const sendConnection = () => {
        if (!window.chrome.runtime) {
            notifyError(t("poll.extensionConnectionError"));
            return;
        }


        let sessionCopy = JSON.parse(JSON.stringify(session));
        if (currPlatform === TOPHAT) {
            sessionCopy.api = `${config.api.poll}/poll/tophat`;
        } else if (currPlatform === MENTIMETER) {
            sessionCopy.api = `${config.api.poll}/poll/mentimeter`;
        } else if (currPlatform === POLL_EVERYWHERE) {
            sessionCopy.api = `${config.api.poll}/poll/polleverywhere`;
        } else if (currPlatform === WOOCLAP) {
            sessionCopy.api = `${config.api.poll}/poll/wooclap`;
        }

        sendMsgToExtension(getExtensionId(), {session: sessionCopy}).then(response => {
            if(response && response.authToken && response.userId && response.authToken === session.authorization && response.userId === session.userId) {
                setHasConnection(true);
                setExtInstalled(true);
            } else {
                setHasConnection(false);
                notifyError(t("poll.extensionConnectionError"));
            }
        });

        // window.chrome.runtime.sendMessage(getExtensionId(), {session: session}, function(response) {
        //     if(response && response.authToken && response.userId && response.authToken === session.authorization && response.userId === session.userId) {
        //         setHasConnection(true);
        //         setExtInstalled(true);
        //     } else {
        //         setHasConnection(false);
        //         notifyError(t("poll.extensionConnectionError"));
        //     }
        // });
    };
    
    const stepCompleted = (index) => {
        if(index === 0 && isChromiumBrowser() && extInstalled) {
            return true;
        }
        else if (index === 1 && hasConnection) {
            return true;
        }
        else {
            return false;
        }
    };

    return (
        <div style={{display: "flex", flexDirection: "column", width: "100%", height: "100%", alignItems: "center"}}>
            <div style={{maxWidth: 1200, display: "flex", flexDirection: "column",}}>
                <div style={{marginTop: "10px"}}>{t("poll.integrationSubtitle")}</div>
                <div style={{display: "flex", alignItems: "center", marginBottom: "30px", marginTop: "20px"}}>
                    {t("integration.selectProduct")}
                    <YuJaDropdown containerStyle={{width: "370px", marginLeft: "20px"}} value={currPlatform}
                                  data={integratedProducts} onChange={(item) => {
                        setCurrPlatform(item.value)
                    }}></YuJaDropdown>
                </div>
                {steps.map((step, index) => {
                    return (<>
                        {!stepCompleted(index) &&
                            <div className={styles.containerStep} key={index}>
                                <div style={{display: "flex", alignItems: "center"}}>
                                    <div className={styles.stepNo}>{index + 1}</div>
                                    <div style={{marginLeft: "15px", fontWeight: "700"}}>{step.title}</div>
                                    <div className={styles.line}></div>
                                </div>
                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    height: "100%"
                                }}>
                                    <div style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        flexDirection: "column",
                                        paddingTop: "10px",
                                        height: "100%",
                                        gap: 10
                                    }}>
                                        {step.subtitle && <div >{step.subtitle}</div>}
                                        {step.button && step.button === "Install Extension" &&
                                            <YuJaButton
                                                onClick={() => window.open(getExtUrl())}>{step.button}</YuJaButton>
                                        }
                                        {step.button && step.button === "Connect" &&
                                            <YuJaButton onClick={sendConnection}>{step.button}</YuJaButton>
                                        }
                                        {step.substeps && step.substeps.map((substep) => {
                                            return <div key={substep}>{substep}</div>
                                        })
                                        }
                                    </div>
                                    <div>
                                        <img src={step.img}
                                             className={index === 2 ? styles.extImage : styles.defaultImg}
                                             alt={t("integration.chromeExtensionStepAlt") + index}/>
                                        {index === 0 &&
                                            <div>{t("integration.pluginInstallHelp")} <a href={getExtSupport()}
                                                                                        target="_blank">{t("integration.clickHere")}</a>.
                                            </div>}
                                    </div>
                                </div>
                            </div>}
                        {stepCompleted(index) && <div className={styles.completedContainer} key={index}>
                            <div>{step.completed}</div>
                            <Checkmark/>
                        </div>}
                    </>)
                })}

            </div>
        </div>

    );
};
